// =============================================================================
// BP-08-05: Vendor Portal Frontend - VendorAnalyticsPage
// =============================================================================

import React from 'react';
import { Calendar, TrendingUp, Eye, MessageSquare, DollarSign, Package } from 'lucide-react';
import { VendorHeader } from '@/features/vendor/components/VendorHeader';
import {
  ViewsChart,
  InquiriesChart,
  RevenueChart,
  TopProductsChart,
} from '@/features/vendor/components/AnalyticsCharts';
import {
  useVendorAnalytics,
  useVendorViews,
  useVendorInquiries,
  useVendorRevenue,
  useTopProducts,
} from '@/features/vendor/hooks/useVendorAnalytics';
import { useVendorStore } from '@/features/vendor/stores/vendorStore';
import type { AnalyticsTimeframe } from '@/features/vendor/types/vendorTypes';

const TIMEFRAME_OPTIONS: { value: AnalyticsTimeframe; label: string }[] = [
  { value: '7d', label: 'Last 7 days' },
  { value: '30d', label: 'Last 30 days' },
  { value: '90d', label: 'Last 90 days' },
  { value: '12m', label: 'Last 12 months' },
];

export function VendorAnalyticsPage() {
  const { analyticsTimeframe, setAnalyticsTimeframe } = useVendorStore();
  const { data: analytics, isLoading } = useVendorAnalytics(analyticsTimeframe);
  const { data: viewsData } = useVendorViews(analyticsTimeframe);
  const { data: inquiriesData } = useVendorInquiries(analyticsTimeframe);
  const { data: revenueData } = useVendorRevenue(analyticsTimeframe);
  const { data: topProducts } = useTopProducts(analyticsTimeframe);

  const formatCurrency = (amount: number) => {
    if (amount >= 1000) {
      return `$${(amount / 1000).toFixed(1)}K`;
    }
    return `$${amount}`;
  };

  const formatNumber = (num: number) => {
    if (num >= 1000) {
      return `${(num / 1000).toFixed(1)}K`;
    }
    return num.toString();
  };

  return (
    <div className="min-h-screen">
      <VendorHeader
        title="Analytics"
        subtitle="Track your store's performance"
        actions={
          <div className="flex items-center gap-2">
            <Calendar className="w-5 h-5 text-warm-gray" />
            <select
              value={analyticsTimeframe}
              onChange={(e) => setAnalyticsTimeframe(e.target.value as AnalyticsTimeframe)}
              className="px-4 py-2 border border-gray-200 rounded-lg bg-white focus:ring-2 focus:ring-terracotta/20"
            >
              {TIMEFRAME_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>
        }
      />

      <div className="p-6 space-y-6">
        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <SummaryCard
            label="Total Views"
            value={formatNumber(analytics?.totalViews || 0)}
            change={analytics?.viewsChange}
            icon={Eye}
            iconBg="bg-blue-100"
            iconColor="text-blue-600"
            isLoading={isLoading}
          />
          <SummaryCard
            label="Inquiries"
            value={formatNumber(analytics?.totalInquiries || 0)}
            change={analytics?.inquiriesChange}
            icon={MessageSquare}
            iconBg="bg-purple-100"
            iconColor="text-purple-600"
            isLoading={isLoading}
          />
          <SummaryCard
            label="Revenue"
            value={formatCurrency(analytics?.totalRevenue || 0)}
            change={analytics?.revenueChange}
            icon={DollarSign}
            iconBg="bg-green-100"
            iconColor="text-green-600"
            isLoading={isLoading}
          />
          <SummaryCard
            label="Orders"
            value={formatNumber(analytics?.totalOrders || 0)}
            change={analytics?.ordersChange}
            icon={Package}
            iconBg="bg-amber-100"
            iconColor="text-amber-600"
            isLoading={isLoading}
          />
        </div>

        {/* Charts Row 1 */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {viewsData && <ViewsChart data={viewsData} />}
          {inquiriesData && <InquiriesChart data={inquiriesData} />}
        </div>

        {/* Charts Row 2 */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {revenueData && <RevenueChart data={revenueData} />}
          {topProducts && <TopProductsChart products={topProducts} />}
        </div>

        {/* Performance Insights */}
        {analytics && (
          <div className="bg-white rounded-xl border border-gray-200 p-6">
            <h2 className="font-display text-lg font-semibold text-navy mb-6">
              Performance Insights
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <InsightCard
                title="Conversion Rate"
                value={`${analytics.conversionRate.toFixed(1)}%`}
                description="Views to inquiries"
                trend={analytics.conversionRate > 5 ? 'good' : 'neutral'}
              />
              <InsightCard
                title="Avg Order Value"
                value={formatCurrency(analytics.avgOrderValue)}
                description="Per completed order"
                trend="neutral"
              />
              <InsightCard
                title="Response Time"
                value={`${analytics.avgResponseTime}h`}
                description="Average inquiry response"
                trend={analytics.avgResponseTime < 24 ? 'good' : 'warning'}
              />
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

interface SummaryCardProps {
  label: string;
  value: string;
  change?: number;
  icon: React.ElementType;
  iconBg: string;
  iconColor: string;
  isLoading?: boolean;
}

function SummaryCard({
  label,
  value,
  change,
  icon: Icon,
  iconBg,
  iconColor,
  isLoading,
}: SummaryCardProps) {
  if (isLoading) {
    return (
      <div className="bg-white rounded-xl border border-gray-200 p-5 animate-pulse">
        <div className="flex items-start justify-between">
          <div className="space-y-2">
            <div className="h-4 w-20 bg-gray-200 rounded" />
            <div className="h-8 w-24 bg-gray-200 rounded" />
          </div>
          <div className="w-12 h-12 bg-gray-200 rounded-xl" />
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-xl border border-gray-200 p-5">
      <div className="flex items-start justify-between">
        <div>
          <p className="text-sm text-warm-gray mb-1">{label}</p>
          <p className="text-2xl font-display font-semibold text-navy">{value}</p>
          {change !== undefined && (
            <div
              className={`flex items-center gap-1 mt-2 text-sm font-medium ${
                change >= 0 ? 'text-green-600' : 'text-red-600'
              }`}
            >
              <TrendingUp className={`w-4 h-4 ${change < 0 ? 'rotate-180' : ''}`} />
              <span>{change >= 0 ? '+' : ''}{change}%</span>
              <span className="text-warm-gray font-normal">vs prev period</span>
            </div>
          )}
        </div>
        <div className={`w-12 h-12 ${iconBg} rounded-xl flex items-center justify-center`}>
          <Icon className={`w-6 h-6 ${iconColor}`} />
        </div>
      </div>
    </div>
  );
}

interface InsightCardProps {
  title: string;
  value: string;
  description: string;
  trend: 'good' | 'warning' | 'neutral';
}

function InsightCard({ title, value, description, trend }: InsightCardProps) {
  const trendColors = {
    good: 'border-green-200 bg-green-50',
    warning: 'border-amber-200 bg-amber-50',
    neutral: 'border-gray-200 bg-gray-50',
  };

  return (
    <div className={`rounded-xl border p-4 ${trendColors[trend]}`}>
      <h3 className="text-sm font-medium text-warm-gray mb-1">{title}</h3>
      <p className="text-xl font-display font-semibold text-navy">{value}</p>
      <p className="text-xs text-warm-gray mt-1">{description}</p>
    </div>
  );
}

export default VendorAnalyticsPage;

